import { motion } from "framer-motion"
import { Clock, User, Users } from "lucide-react"
import type { Appointment } from "../../../types"

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString("es-CR", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })
}

const TYPE_STYLES = {
  personal: {
    border: "border-primary/40",
    bg: "bg-primary/10",
    text: "text-primary",
    dot: "bg-primary",
  },
  group_class: {
    border: "border-amber-400/40",
    bg: "bg-amber-400/10",
    text: "text-amber-400",
    dot: "bg-amber-400",
  },
}

const STATUS_OVERRIDES: Record<string, { border: string; bg: string; text: string; dot: string }> = {
  completed: {
    border: "border-emerald-400/40",
    bg: "bg-emerald-400/10",
    text: "text-emerald-400",
    dot: "bg-emerald-400",
  },
  cancelled: {
    border: "border-red-400/40",
    bg: "bg-red-400/10",
    text: "text-red-400",
    dot: "bg-red-400",
  },
}

export default function AppointmentCard({
  appointment,
  compact = false,
  onClick,
}: {
  appointment: Appointment
  compact?: boolean
  onClick?: () => void
}) {
  const style =
    STATUS_OVERRIDES[appointment.status] ?? TYPE_STYLES[appointment.appointment_type]

  function handleDragStart(e: React.DragEvent) {
    e.dataTransfer.setData("application/appointment-id", String(appointment.id))
    e.dataTransfer.effectAllowed = "move"
  }

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      className="cursor-grab active:cursor-grabbing"
    >
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={onClick}
        className={`w-full text-left rounded-lg border ${style.border} ${style.bg} p-2 transition-all hover:shadow-lg`}
      >
        <div className="flex items-start gap-1.5">
          <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${style.dot}`} />
          <div className="min-w-0 flex-1">
            <p className={`text-xs font-semibold truncate ${style.text}`}>
              {appointment.participants.map((p) => p.name).join(", ") || "Sin asignar"}
            </p>
            {!compact && (
              <>
                <p className="mt-0.5 flex items-center gap-1 text-[10px] text-muted">
                  <Clock size={10} />
                  {formatTime(appointment.start_time)} - {formatTime(appointment.end_time)}
                </p>
                <p className="mt-0.5 flex items-center gap-1 text-[10px] text-muted">
                  {appointment.appointment_type === "personal" ? (
                    <User size={10} />
                  ) : (
                    <Users size={10} />
                  )}
                  {appointment.title}
                </p>
              </>
            )}
          </div>
        </div>
      </motion.button>
    </div>
  )
}
